/**
 * OAuthCallback.jsx
 *
 * Brokers redirect to /broker-callback/:broker with auth params:
 *   Zerodha  → ?request_token=...&action=login&status=success
 *   Upstox   → ?code=...&state=upstox
 *   Fyers    → ?auth_code=...&state=fyers
 *
 * This page:
 *  1. Reads all query params
 *  2. Calls GET /api/broker/oauth/callback/:broker with those params
 *     (the axios client auto-attaches Authorization: Bearer <jwt> from localStorage)
 *  3. On success → notifies the opener and closes the popup
 *  4. On failure → shows the error so the user can read it
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";

export default function OAuthCallback() {
  const { broker } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const exchange = async () => {
      // Guard: JWT must be in localStorage (popup is same-origin, so it's shared)
      const token = localStorage.getItem("algo_token");
      if (!token) {
        setMessage("You are not logged in. Please log in to AlgoDesk first, then retry broker login.");
        setStatus("error");
        return;
      }

      // Collect every query param the broker sends
      const params = Object.fromEntries(searchParams.entries());

      // Surface broker-side errors immediately
      if (params.error) {
        setMessage(params.error_description || params.error);
        setStatus("error");
        return;
      }

      // Zerodha sends status=success in the redirect; surface a failure early
      if (params.status && params.status !== "success") {
        setMessage(`${broker} login failed (status: ${params.status})`);
        setStatus("error");
        return;
      }

      try {
        // axios client attaches Authorization: Bearer <jwt> automatically
        await api.get(`/broker/oauth/callback/${broker}`, { params });

        setStatus("success");
        setMessage(`${broker} connected — window will close.`);

        // Notify the opener to refresh its broker list
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.postMessage({ type: "BROKER_AUTH_SUCCESS", broker }, window.location.origin);
          } catch (_) {}
        }

        // Close the popup after a brief delay so the user sees the ✓
        setTimeout(() => {
          try { window.close(); } catch (_) {}
          // Fallback if close() was blocked
          if (!window.closed) window.location.href = "/settings";
        }, 1800);

      } catch (err) {
        const detail = err?.response?.data?.detail || err.message || "Token exchange failed";
        setMessage(detail);
        setStatus("error");
      }
    };

    exchange();
  }, []); // runs once on mount — params are stable from the URL

  const brokerLabel = broker ? broker.charAt(0).toUpperCase() + broker.slice(1) : "Broker";

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
      <div className="max-w-sm w-full bg-[#141414] border border-white/10 rounded-sm p-8 text-center space-y-4">

        {status === "loading" && (
          <>
            <div className="mx-auto w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-300 text-sm">Completing {brokerLabel} login…</p>
            <p className="text-slate-500 text-xs">Exchanging token with broker…</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="mx-auto w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-emerald-400 font-semibold">{brokerLabel} Connected!</p>
            <p className="text-slate-400 text-xs">{message}</p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="mx-auto w-10 h-10 rounded-full bg-rose-500/15 flex items-center justify-center">
              <svg className="w-5 h-5 text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-rose-400 font-semibold">Login Failed</p>
            <p className="text-slate-400 text-xs break-words">{message}</p>
            <button
              onClick={() => window.close()}
              className="mt-2 text-xs text-slate-500 underline hover:text-slate-300"
            >
              Close this window
            </button>
          </>
        )}

      </div>
    </div>
  );
}
